import type * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert } from './ui/Alert'
import { Button } from './ui/Button'
import { Card, CardHeader } from './ui/Card'
import { Input } from './ui/Input'
import {
  fetchTranslations,
  regenerateTranslation,
  saveTranslation,
  type TranslationEntity,
  type TranslationStatus,
  type TranslationView,
} from '../services/translationService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'
import { STORE_LANGUAGES } from '../utils/storeLanguages'
import { cn } from '@/lib/utils'

export type TranslationFieldDef = { key: string; label: string; multiline?: boolean; rows?: number }

/** Where an item's translations are loaded from and saved to. */
export type TranslationsAdapter = {
  load: () => Promise<TranslationView>
  save: (locale: string, values: Record<string, string>) => Promise<TranslationView>
  regenerate: (locale: string) => Promise<TranslationView>
}

type Props = {
  /** Products and categories: the editor builds its own adapter. */
  entity?: TranslationEntity
  entityId?: number
  /** Anything else (banners, offers, store text): bring your own. Must be stable between renders. */
  adapter?: TranslationsAdapter
  /** Already-loaded data (skips the first request). */
  initialView?: TranslationView
  /** Render without the surrounding card (inside a list of items). */
  embedded?: boolean
  fields: TranslationFieldDef[]
  /** Change this (e.g. the item's `updatedAt`) to re-read translations after the item was saved. */
  reloadKey?: string | number
}

const STATUS_LABEL: Record<TranslationStatus, string> = {
  missing: 'Not translated yet',
  auto: 'Auto-translated',
  edited: 'Edited by you',
  stale: 'Needs review',
}

const STATUS_STYLE: Record<TranslationStatus, string> = {
  missing: 'bg-muted text-muted-foreground',
  auto: 'bg-primary/10 text-primary',
  edited: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  stale: 'bg-amber-500/15 text-amber-800 dark:text-amber-200',
}

const areaClass =
  'w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-60'

function languageName(code: string): string {
  return STORE_LANGUAGES.find((l) => l.code === code)?.label.split(' (')[0] ?? code.toUpperCase()
}

/**
 * Per-language editor for merchant text. Automatic translations appear a few seconds after the item is saved;
 * the merchant can edit any of them, and edited versions are never overwritten automatically.
 */
export function TranslationsEditor({ entity, entityId, adapter, initialView, embedded = false, fields, reloadKey }: Props) {
  const api = useMemo<TranslationsAdapter>(
    () =>
      adapter ?? {
        load: () => fetchTranslations(entity as TranslationEntity, entityId as number),
        save: (locale, values) => saveTranslation(entity as TranslationEntity, entityId as number, locale, values),
        regenerate: (locale) => regenerateTranslation(entity as TranslationEntity, entityId as number, locale),
      },
    [adapter, entity, entityId],
  )
  const [view, setView] = useState<TranslationView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [active, setActive] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({})
  const [busy, setBusy] = useState<'save' | 'regenerate' | null>(null)
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const pollsLeft = useRef(5)

  const applyView = useCallback((v: TranslationView) => {
    setView(v)
    setDrafts(
      Object.fromEntries(
        v.targetLocales.map((l) => [
          l,
          Object.fromEntries(fields.map((f) => [f.key, v.translations[l]?.fields[f.key]?.value ?? ''])),
        ]),
      ),
    )
    setActive((cur) => (cur && v.targetLocales.includes(cur) ? cur : (v.targetLocales[0] ?? null)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const usedInitial = useRef(false)
  useEffect(() => {
    let cancelled = false
    pollsLeft.current = 5
    setLoadError(null)
    if (initialView && !usedInitial.current) {
      usedInitial.current = true
      applyView(initialView)
      return () => {
        cancelled = true
      }
    }
    api
      .load()
      .then((v) => {
        if (!cancelled) applyView(v)
      })
      .catch((e) => {
        if (!cancelled) setLoadError(getUserFacingErrorMessage(e, 'Failed to load translations'))
      })
    return () => {
      cancelled = true
    }
  }, [api, reloadKey, applyView, initialView])

  // Right after saving an item the automatic translations arrive a few seconds later: check back briefly.
  useEffect(() => {
    if (!view?.autoTranslationAvailable || pollsLeft.current <= 0) return
    const waiting = view.targetLocales.some((l) => view.translations[l]?.status === 'missing')
    if (!waiting || busy) return
    const t = setTimeout(() => {
      pollsLeft.current -= 1
      api
        .load()
        .then((v) => {
          // Keep what the merchant is typing; only fill languages they haven't touched.
          setView(v)
          setDrafts((prev) => {
            const next = { ...prev }
            for (const l of v.targetLocales) {
              const stored = Object.fromEntries(fields.map((f) => [f.key, v.translations[l]?.fields[f.key]?.value ?? '']))
              const untouched = !prev[l] || fields.every((f) => (prev[l]?.[f.key] ?? '') === '')
              if (untouched) next[l] = stored
            }
            return next
          })
        })
        .catch(() => {
          /* try again on the next tick */
        })
    }, 3000)
    return () => clearTimeout(t)
  }, [view, busy, api, fields])

  const tab = active && view ? view.translations[active] : null
  const stored = useCallback(
    (locale: string, key: string) => view?.translations[locale]?.fields[key]?.value ?? '',
    [view],
  )
  const dirty = useMemo(() => {
    if (!active || !drafts[active]) return false
    return fields.some((f) => (drafts[active]?.[f.key] ?? '').trim() !== stored(active, f.key))
  }, [active, drafts, fields, stored])

  async function onSave() {
    if (!active) return
    setBusy('save')
    setNotice(null)
    try {
      applyView(await api.save(active, drafts[active] ?? {}))
      setNotice({ kind: 'success', text: `${languageName(active)} translation saved.` })
    } catch (e) {
      setNotice({ kind: 'error', text: getUserFacingErrorMessage(e, 'Could not save the translation') })
    } finally {
      setBusy(null)
    }
  }

  async function onKeepMine() {
    if (!active) return
    setBusy('save')
    setNotice(null)
    try {
      const keep = Object.fromEntries(fields.map((f) => [f.key, stored(active, f.key)]))
      applyView(await api.save(active, keep))
      setNotice({ kind: 'success', text: 'Kept your version.' })
    } catch (e) {
      setNotice({ kind: 'error', text: getUserFacingErrorMessage(e, 'Could not update the translation') })
    } finally {
      setBusy(null)
    }
  }

  async function onRegenerate() {
    if (!active || !tab) return
    if (
      (tab.status === 'edited' || tab.status === 'stale') &&
      !window.confirm(`Replace your ${languageName(active)} wording with a new automatic translation?`)
    ) {
      return
    }
    setBusy('regenerate')
    setNotice(null)
    try {
      applyView(await api.regenerate(active))
      setNotice({ kind: 'success', text: `${languageName(active)} translated again.` })
    } catch (e) {
      setNotice({ kind: 'error', text: getUserFacingErrorMessage(e, 'Could not translate right now') })
    } finally {
      setBusy(null)
    }
  }

  /** Card with a heading on a page of its own; bare content when listed among other items. */
  const shell = (children: React.ReactNode, description?: string) =>
    embedded ? (
      <div className="space-y-4">{children}</div>
    ) : (
      <Card>
        <CardHeader title="Translations" description={description} />
        {children}
      </Card>
    )

  if (loadError) {
    return shell(
      <Alert variant="destructive" title="Could not load translations">
        {loadError}
      </Alert>,
    )
  }
  if (!view) return null

  return shell(
    <>

      {view.targetLocales.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Your shop is shown in one language. Add more under{' '}
          <Link to="/settings" className="font-medium text-primary hover:underline">
            Settings → Storefront languages
          </Link>{' '}
          to translate this item.
        </p>
      ) : (
        <div className="space-y-5">
          {!view.autoTranslationAvailable ? (
            <Alert variant="info" title="Automatic translation is not set up yet">
              You can still write each language by hand. Shoppers see your original text wherever a translation is
              missing.
            </Alert>
          ) : null}

          <div role="tablist" aria-label="Translation languages" className="flex flex-wrap gap-2">
            {view.targetLocales.map((l) => {
              const status = view.translations[l]?.status ?? 'missing'
              return (
                <button
                  key={l}
                  type="button"
                  role="tab"
                  aria-selected={l === active}
                  onClick={() => {
                    setActive(l)
                    setNotice(null)
                  }}
                  className={cn(
                    'flex items-center gap-2 rounded-2xl border px-3.5 py-2 text-sm transition-colors',
                    l === active ? 'border-ring bg-muted font-medium' : 'border-border hover:bg-muted/60',
                  )}
                >
                  {languageName(l)}
                  <span className={cn('rounded-full px-2 py-0.5 text-xs', STATUS_STYLE[status])}>{STATUS_LABEL[status]}</span>
                </button>
              )
            })}
          </div>

          {active && tab ? (
            <div className="space-y-4">
              {tab.status === 'stale' ? (
                <Alert variant="warning" title="The original text changed">
                  You edited this language earlier. Check it still matches the new original, or reset it to the
                  automatic translation.
                </Alert>
              ) : null}

              {fields.map((f) => {
                const original = view.source[f.key] ?? ''
                if (!original && !(drafts[active]?.[f.key] ?? '')) return null
                const common = {
                  id: `tr-${active}-${f.key}`,
                  value: drafts[active]?.[f.key] ?? '',
                  placeholder: original ? 'Not translated yet — shoppers see the original text' : '',
                  disabled: busy !== null,
                  onChange: (e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) =>
                    setDrafts((d) => ({ ...d, [active]: { ...d[active], [f.key]: e.target.value } })),
                }
                return (
                  <div key={f.key} className="space-y-1.5">
                    {f.multiline ? (
                      <>
                        <label htmlFor={common.id} className="text-sm font-medium text-foreground">
                          {f.label}
                        </label>
                        <textarea {...common} rows={f.rows ?? 4} className={areaClass} />
                      </>
                    ) : (
                      <Input label={f.label} {...common} />
                    )}
                    {original ? (
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {languageName(view.sourceLocale)} original: {original}
                      </p>
                    ) : null}
                  </div>
                )
              })}

              {notice ? (
                <p className={cn('text-sm', notice.kind === 'error' ? 'text-destructive' : 'text-emerald-700 dark:text-emerald-300')} role="status">
                  {notice.text}
                </p>
              ) : null}

              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
                <Button type="button" onClick={() => void onSave()} disabled={!dirty || busy !== null}>
                  {busy === 'save' ? 'Saving…' : 'Save translation'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void onRegenerate()}
                  disabled={busy !== null || !view.autoTranslationAvailable}
                >
                  {busy === 'regenerate'
                    ? 'Translating…'
                    : tab.status === 'edited' || tab.status === 'stale'
                      ? 'Reset to automatic translation'
                      : tab.status === 'missing'
                        ? 'Translate now'
                        : 'Translate again'}
                </Button>
                {tab.status === 'stale' ? (
                  <Button type="button" variant="ghost" onClick={() => void onKeepMine()} disabled={busy !== null}>
                    Keep my version
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </>,
    `Write in ${languageName(view.sourceLocale)}. Other languages are translated automatically, and you can edit any of them.`,
  )
}
